import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchUnreadSystemMessages } from '@/utils/system-messages-unread';

export const useUnreadSystemMessages = (userType: 'client' | 'practitioner') => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    try {
      const result = await fetchUnreadSystemMessages({
        userId: user.id,
        userEmail: user.email,
        userType,
      });

      console.log('[useUnreadSystemMessages] persisted unread-count query', {
        query: result.query,
        userId: user.id,
        userType,
        ...result.debug,
      });

      setUnreadCount(result.debug.finalUnreadCount);
    } catch (err) {
      console.error('Unexpected error fetching unread messages:', err);
      setUnreadCount(0);
    }
  }, [user, userType]);

  useEffect(() => {
    fetchUnreadCount();

    if (!user) return;

    const channel = supabase
      .channel('unread-system-messages')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'system_messages' },
        () => fetchUnreadCount()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'system_message_reads', filter: `user_id=eq.${user.id}` },
        () => fetchUnreadCount()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'system_message_dismissals', filter: `user_id=eq.${user.id}` },
        () => fetchUnreadCount()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, userType, fetchUnreadCount]);

  // Kept for API compatibility; reads are now persisted per-message via Mark as Read.
  const markAsViewed = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return { unreadCount, markAsViewed, refreshUnreadCount: fetchUnreadCount };
};
